import React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  GraduationCap,
  MapPin,
  Mail,
  Phone,
  Award,
  BookOpen,
  ShieldCheck,
} from "lucide-react";
const AboutCollege = () => {
  const highlights = [
    {
      icon: <BookOpen size={22} />,
      title: "Quality Education",
    },
    {
      icon: <GraduationCap size={22} />,
      title: "Higher Education",
    },
    {
      icon: <ShieldCheck size={22} />,
      title: "Discipline & Values",
    },
    {
      icon: <Award size={22} />,
      title: "Academic Excellence",
    },
  ];
  return (
    <section className="w-full bg-[#F6F8FC] py-10 md:py-12 overflow-hidden">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-9"
          >
            <span className="inline-flex items-center gap-2 bg-[#071A52] text-[#FFD700] px-5 py-2 rounded-full text-xs font-black uppercase tracking-wide mb-4 shadow">
              <Building2 size={15} />
              About College
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-[#071A52] leading-tight">
              Pt. Shrinivas Dwivedi Mahavidyalaya
            </h2>
            <div className="w-24 h-1.5 bg-[#FFD700] rounded-full mx-auto mt-4 mb-4"></div>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              A reputed higher education institution located at Arangi,
              Chandauli, Uttar Pradesh.
            </p>
          </motion.div>
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-5 relative"
            >
              <div className="absolute -top-4 -left-4 w-full h-full bg-[#FFD700] rounded-[28px]"></div>
              <div className="relative bg-white rounded-[28px] overflow-hidden shadow-xl border border-gray-100">
                <img
                  src="/image1.png"
                  alt="Pt. Shrinivas Dwivedi Mahavidyalaya"
                  className="w-full h-[300px] md:h-[420px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071A52]/75 via-transparent to-transparent"></div>

                <div className="absolute bottom-5 left-5 right-5 bg-white/90 backdrop-blur-md rounded-2xl p-4 shadow-lg">
                  <h3 className="text-lg font-black text-[#071A52]">
                    Pt. Shrinivas Dwivedi Mahavidyalaya
                  </h3>
                  <p className="text-sm text-gray-700 mt-1 font-medium">
                    Arangi, Chandauli, Uttar Pradesh
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 35 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-7"
            >
              <div className="bg-white rounded-[28px] shadow-xl border border-gray-100 p-6 md:p-8">
                <span className="text-[#D4A017] text-sm font-black uppercase tracking-wide">
                  Welcome To Our College
                </span>
                <h3 className="text-2xl md:text-4xl font-black text-[#071A52] mt-3 mb-5 leading-tight">
                  Building Knowledge, Discipline & Future
                </h3>
                <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-4">
                  Pt. Shrinivas Dwivedi Mahavidyalaya is a reputed educational
                  institution dedicated to providing quality higher education.
                  The college focuses on academic excellence, discipline, moral
                  values, and overall student development.
                </p>
                <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-6">
                  The institution aims to empower students with knowledge,
                  confidence, and a strong academic foundation so they can move
                  forward successfully in their future.
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  {highlights.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 18 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.08 }}
                      viewport={{ once: true }}
                      className="group flex items-center gap-3 bg-[#F6F8FC] rounded-2xl p-4 border border-gray-100 hover:bg-[#071A52] hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                    >
                      <div className="w-11 h-11 rounded-xl bg-[#071A52] text-[#FFD700] flex items-center justify-center group-hover:bg-[#FFD700] group-hover:text-[#071A52] transition-all">
                        {item.icon}
                      </div>
                      <p className="text-sm font-black text-[#071A52] group-hover:text-white transition-all">
                        {item.title}
                      </p>
                    </motion.div>
                  ))}
                </div>
                <div className="rounded-2xl bg-[#071A52] p-5 text-white">
                  <h4 className="text-lg font-black text-[#FFD700] mb-4">
                    College Details
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <p className="flex gap-3">
                      <MapPin
                        size={18}
                        className="text-[#FFD700] shrink-0 mt-0.5"
                      />
                      <span>
                        Village & Post Arangi, District Chandauli, Uttar
                        Pradesh - 232110
                      </span>
                    </p>
                    <p className="flex gap-3">
                      <Award
                        size={18}
                        className="text-[#FFD700] shrink-0 mt-0.5"
                      />
                      <span>
                        Affiliated To: Mahatma Gandhi Kashi Vidyapith,
                        Varanasi
                      </span>
                    </p>
                    <p className="flex gap-3">
                      <Mail
                        size={18}
                        className="text-[#FFD700] shrink-0 mt-0.5"
                      />
                      <span>psndwivedi@gmail.com</span>
                    </p>
                    <p className="flex gap-3">
                      <Phone
                        size={18}
                        className="text-[#FFD700] shrink-0 mt-0.5"
                      />
                      <span>+91-9451887217, +91-9450181867</span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default AboutCollege;